import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GetData, SERVER_URL } from '../utils/api';
import ProductCard from '../components/common/ProductCard';
import './Landing.css';

const Landing = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [services, setServices] = useState([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBanner((prev) => (prev + 1) % banners.length);
      }, 5000); // Cambiar banner cada 5 segundos
      return () => clearInterval(interval);
    }
  }, [banners]);

  const loadData = async () => {
    try {
      const [productsData, bannersData, servicesData] = await Promise.all([
        GetData('/products?featured=true'),
        GetData('/banners'),
        GetData('/services')
      ]);
      setFeaturedProducts(productsData.products || []);
      setBanners(bannersData.banners || []);
      setServices(servicesData.services || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToBanner = (index) => {
    setCurrentBanner(index);
  };

  return (
    <div className="landing">
      {/* Banner Carousel */}
      {banners.length > 0 && (
        <section className="banner-carousel">
          <div className="banner-slider">
            {banners.map((banner, index) => (
              <div
                key={banner.id}
                className={`banner-slide ${index === currentBanner ? 'active' : ''}`}
              >
                <div 
                  className="banner-image-wrapper"
                style={{
                    backgroundImage: `url(${SERVER_URL}${banner.image})`,
                  backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
              >
                  {(banner.title || banner.subtitle) && <div className="banner-overlay"></div>}
                </div>
                {(banner.title || banner.subtitle || (banner.buttonText && banner.buttonLink)) && (
                  <div className="container">
                    <div className="banner-content">
                      {banner.title && <h2 className="banner-title">{banner.title}</h2>}
                      {banner.subtitle && <p className="banner-subtitle">{banner.subtitle}</p>}
                      {banner.buttonText && banner.buttonLink && (
                        <Link to={banner.buttonLink} className="btn btn-primary btn-lg">
                          {banner.buttonText}
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {banners.length > 1 && (
            <>
              <button className="banner-arrow banner-arrow-left" onClick={prevBanner}>
                ‹
              </button>
              <button className="banner-arrow banner-arrow-right" onClick={nextBanner}>
                ›
              </button>

              <div className="banner-dots">
                {banners.map((_, index) => (
                  <button
                    key={index}
                    className={`banner-dot ${index === currentBanner ? 'active' : ''}`}
                    onClick={() => goToBanner(index)}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container hero-container">
          <div className="hero-content">
            <span className="hero-badge">Nuevos Productos Disponibles</span>
            <h1 className="hero-title">
              Descubre los Mejores
              <span className="hero-highlight"> Productos</span>
              <br />
              Al Mejor Precio
            </h1>
            <p className="hero-description">
              Explora nuestra colección exclusiva de productos seleccionados especialmente para ti.
              Calidad garantizada y envío gratis en compras mayores a $50.
            </p>
            <div className="hero-buttons">
              <Link to="/products" className="btn btn-primary btn-lg">
                Ver Catálogo
              </Link>
              <Link to="/products" className="btn btn-secondary btn-lg">
                Explorar Más
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Featured Products */}
      <section className="featured-products-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Destacados</span>
            <h2 className="section-title">Productos Más Vendidos</h2>
            <p className="section-description">
              Descubre los productos favoritos de nuestros clientes
            </p>
          </div>

          {loading ? (
            <div className="loading">Cargando productos...</div>
          ) : featuredProducts.length === 0 ? (
            <p className="text-center">No hay productos destacados</p>
          ) : (
            <div className="grid grid-4">
              {featuredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="section-cta">
            <Link to="/products" className="btn btn-primary btn-lg">
              Ver Todos los Productos
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      {services.length > 0 && (
        <section className="services-section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge">Servicios</span>
              <h2 className="section-title">Nuestros Servicios</h2>
              <p className="section-description">
                Conoce los servicios que ofrecemos para ti
              </p>
            </div>

            <div className="services-grid">
              {services.map(service => (
                <div key={service.id} className="service-card-landing">
                  {service.image && (
                    <div className="service-card-image">
                      <img src={`${SERVER_URL}${service.image}`} alt={service.name} />
                    </div>
                  )}
                  <div className="service-card-body">
                    <h3 className="service-card-title">{service.name}</h3>
                    {service.description && (
                      <p className="service-card-description">{service.description}</p>
                    )}
                    <div className="service-card-footer">
                      {service.price != null && (
                        <span className="service-card-price">${service.price.toFixed(2)}</span>
                      )}
                      {service.schedule && (
                        <span className="service-card-schedule">{service.schedule}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Beneficios</span>
            <h2 className="section-title">¿Por Qué Elegirnos?</h2>
            <p className="section-description">
              Ofrecemos la mejor experiencia de compra con garantías y beneficios exclusivos
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <h3 className="feature-title">Envío Gratis</h3>
              <p className="feature-description">
                Disfruta de envío gratuito en todas tus compras mayores a $50.
                Entregas rápidas y seguras.
              </p>
            </div>

            <div className="feature-card">
              <h3 className="feature-title">Pago Seguro</h3>
              <p className="feature-description">
                Múltiples métodos de pago con tecnología de encriptación de última generación.
              </p>
            </div>

            <div className="feature-card">
              <h3 className="feature-title">Garantía 30 Días</h3>
              <p className="feature-description">
                Devoluciones sin complicaciones. Si no estás satisfecho, te devolvemos tu dinero.
              </p>
            </div>

            <div className="feature-card">
              <h3 className="feature-title">Calidad Premium</h3>
              <p className="feature-description">
                Productos cuidadosamente seleccionados con los más altos estándares de calidad.
              </p>
            </div>

            <div className="feature-card">
              <h3 className="feature-title">Soporte 24/7</h3>
              <p className="feature-description">
                Nuestro equipo está disponible las 24 horas para ayudarte con cualquier consulta.
              </p>
            </div>

            <div className="feature-card">
              <h3 className="feature-title">Ofertas Exclusivas</h3>
              <p className="feature-description">
                Accede a descuentos especiales y promociones exclusivas para miembros.
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Testimonios</span>
            <h2 className="section-title">Lo Que Dicen Nuestros Clientes</h2>
            <p className="section-description">
              Miles de clientes satisfechos confían en nosotros
            </p>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-rating">
                5.0
              </div>
              <p className="testimonial-text">
                "Excelente servicio y productos de calidad. La entrega fue rápida y
                el empaque impecable. Definitivamente volveré a comprar."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">MG</div>
                <div className="author-info">
                  <h4>María González</h4>
                  <p>Cliente Verificado</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-rating">
                5.0
              </div>
              <p className="testimonial-text">
                "La mejor experiencia de compra online que he tenido. Los productos
                superaron mis expectativas y el soporte al cliente es excepcional."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">JR</div>
                <div className="author-info">
                  <h4>Juan Rodríguez</h4>
                  <p>Cliente Verificado</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-rating">
                5.0
              </div>
              <p className="testimonial-text">
                "Increíble variedad de productos y precios muy competitivos.
                El proceso de compra es muy sencillo y seguro. Muy recomendado."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">AS</div>
                <div className="author-info">
                  <h4>Ana Silva</h4>
                  <p>Cliente Verificado</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">¿Listo Para Comenzar?</h2>
            <p className="cta-description">
              Únete a miles de clientes satisfechos y descubre productos increíbles hoy mismo
            </p>
            <Link to="/products" className="btn btn-white btn-lg">
              Explorar Catálogo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
